import React, { useMemo, useRef, useState } from 'react';
import { Menu, MenuOption, MenuOptions, MenuTrigger } from 'react-native-popup-menu';
import MyAppText from './MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { ActivityIndicator, LayoutChangeEvent, StyleProp, TextStyle, View, ViewStyle } from 'react-native';
import { Hoverable } from 'react-native-web-hover';
import { WindowInformation } from '../services/window-information';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { translate } from '../services/translate';
import { ButtonTypes } from './CustomButton';

type Variants = 'primary' | 'danger' | 'warning' | 'success';

export interface Action {
    content: string | React.ReactNode;
    viewStyle?: StyleProp<ViewStyle>;
    textStyle?: StyleProp<TextStyle>;
    disabled?: boolean;
    omit?: boolean;
    variant?: Variants;
    onPress: () => void;
}

interface Props {
    actions: Action[];
    wrapperStyle?: StyleProp<ViewStyle>;
    size?: number;
    offsetX?: number;
    offsetY?: number;
    isButton?: boolean;
    customButton?: React.ReactNode;
    buttonIcon?: boolean | IconDefinition | IconDefinition;
    buttonType?: ButtonTypes;
    isLoading?: boolean;
    buttonLabel?: string;
    disabled?: boolean;
    viewStyle?: StyleProp<ViewStyle>;
}

export default function DropdownButton({ actions, viewStyle, wrapperStyle, offsetX, offsetY, size, isButton, customButton, isLoading = false, buttonLabel, disabled, buttonType = 'secondary', buttonIcon = true }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const ref = useRef<View>(null);
    const [pageYPosition, setPageYPosition] = useState(0);

    const [totalHeight, setTotalHeight] = useState<number>(0);
    const itemHeights = useRef<Record<number, number>>({});

    function onSelectItem() {
        if (ref.current) {
            ref.current.measure((x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
                setPageYPosition(pageY);
            });
        }
    }

    function aggregateItemsHeight(index: number, event: LayoutChangeEvent): void {
        const { height } = event.nativeEvent.layout;
        itemHeights.current[index] = height;

        const total = Object.values(itemHeights.current).reduce((sum: number, h: number) => sum + h, 0);
        setTotalHeight(total);
    }

    const isLowerThird = useMemo(() => {
        const part = windowInfo.height / 3;
        return pageYPosition > (windowInfo.height - part);
    }, [pageYPosition]);

    if (actions.filter(action => !action.omit).length === 0) {
        return <></>;
    }

    return (
        <View style={{ flex: 1 }}>
            <View style={[viewStyle ? viewStyle : {
                width: '100%',
                height: '100%',
            }]} ref={ref}>
                <Menu style={styles.menuStyle}>
                    <MenuTrigger customStyles={{
                        triggerOuterWrapper: {
                            justifyContent: 'center',
                            alignItems: 'center'
                        },
                    }} style={styles.triggerStyle} onPress={onSelectItem}>
                        <Hoverable>
                            {({ hovered }) => (
                                !isButton ?
                                    <View style={styles.iconContainer(hovered)}>
                                        {isLoading
                                            ? <ActivityIndicator size='small' color={styles.loadingColor.color} />
                                            : <FontAwesomeIcon icon={faEllipsisVertical} color={theme.colors.neutral['400']} />
                                        }
                                    </View> :
                                    customButton
                                        ? customButton
                                        : <View style={styles.buttonContainer(buttonType, hovered, disabled || isLoading)}>
                                            {isLoading
                                                ? <ActivityIndicator size='small' color={styles.loadingColor.color} />
                                                : <>
                                                    {buttonIcon && <FontAwesomeIcon style={styles.buttonIcon(buttonType, hovered, disabled)} icon={typeof buttonIcon === 'boolean' ? faPlus as IconDefinition : buttonIcon} />}
                                                    <MyAppText style={styles.buttonText(buttonType, hovered, disabled ?? false)}>{translate(buttonLabel ?? 'more')}</MyAppText>
                                                </>
                                            }
                                        </View>
                            )}
                        </Hoverable>
                    </MenuTrigger>
                    <MenuOptions
                        customStyles={{
                            optionsWrapper: [wrapperStyle ?? styles.wrapperStyle(size, offsetX, offsetY, isLowerThird, totalHeight, isButton), styles.wrapperShadow],
                        }}
                    >
                        {actions.filter(action => !action.omit).map((action, index) => (
                            <Hoverable key={action + index.toString()} style={{ flex: 1 }} onLayout={(event) => aggregateItemsHeight(index, event)}>
                                {({ hovered }) => (
                                    typeof action.content === 'string'
                                        ? <MenuOption disabled={action.disabled} style={{ ...styles.optionStyle(hovered, action.variant ?? 'primary', action.disabled ?? false), ...(action.viewStyle as ViewStyle) }} onSelect={() => action.onPress()}><MyAppText style={{ ...styles.textStyle(action.variant ?? 'primary'), ...(action.textStyle as TextStyle) }}>{action.content}</MyAppText></MenuOption>
                                        : <MenuOption disabled={action.disabled} style={{ ...styles.optionStyle(hovered, action.variant ?? 'primary', action.disabled ?? false), ...(action.viewStyle as ViewStyle) }} onSelect={() => action.onPress()}>{action.content}</MenuOption>
                                )}
                            </Hoverable>
                        ))}
                    </MenuOptions>
                </Menu>
            </View>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    menuStyle: {
        flex: 1
    },
    triggerStyle: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center'
    },
    wrapperStyle: (size: number | undefined, offsetX: number | undefined, offsetY: number | undefined, isLowerThird: boolean, itemsHeight: number, isButton?: boolean) => {
        const usableSize = size ?? 155;
        const diffFromDefaultSize = 155 - usableSize;

        return {
            position: 'absolute',
            minWidth: usableSize,
            maxWidth: usableSize,
            transform: [{ translateX: offsetX ?? -15 + diffFromDefaultSize }, { translateY: isButton && offsetY ? offsetY : offsetY ?? isLowerThird ? -(itemsHeight + 6) : 20 }],
            backgroundColor: theme.colors.white['500'],
            borderColor: theme.colors.neutral['300'],
            borderRadius: 4,
            borderWidth: 0.5,
            flexDirection: 'column',
            padding: 4,
        };
    },
    wrapperShadow: {
        shadowColor: 'black',
        shadowOffset: {
            height: 2,
            width: -1
        },
        shadowOpacity: 0.15,
        shadowRadius: 5,
    },
    textStyle: (variant: Variants) => ({
        fontSize: 14,
        fontWeight: '500',
        color: variant == 'primary' ? theme.colors.neutral['500'] : variant == 'warning' ? theme.colors.warnings['300'] : variant == 'success' ? theme.colors.successes['300'] : theme.colors.errors['300']
    }),
    optionStyle: (hovered: boolean, variant: Variants, disable: boolean) => ({
        padding: 8,
        backgroundColor: disable
            ? theme.colors.neutral['200']
            : hovered
                ? variant === 'primary' ? theme.colors.backgrounds['BG-1'] : variant === 'warning' ? theme.colors.backgrounds['BG-1'] : variant == 'success' ? theme.colors.successes['500'] : theme.colors.backgrounds['BG-5']
                : theme.colors.white['500'],
    }),
    iconContainer: (hovered: boolean) => {
        return {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 5,
            height: 25,
            width: 25,
            backgroundColor: hovered ? theme.colors.neutral['100'] : undefined,
        };
    },
    buttonContainer: (type: ButtonTypes, hovered: boolean, disabled: boolean) => {
        let backgroundColor: string | undefined;
        let borderColor: string | undefined;
        let borderWidth = 0;
        let paddingHorizontal = 20;

        if (type == 'primary') {
            backgroundColor = disabled ? theme.colors.primaryButton.disabledBackground : hovered ? theme.colors.primaryButton.hoverBackground : theme.colors.primaryButton.background;
            borderWidth = 0;
            borderColor = undefined;
        } else if (type == 'secondary') {
            backgroundColor = disabled ? theme.colors.secondaryButton.disabledBackground : hovered ? theme.colors.secondaryButton.hoverBackground : theme.colors.secondaryButton.background;
            borderWidth = 1;
            borderColor = disabled ? theme.colors.secondaryButton.disabledBorderColor : hovered ? theme.colors.secondaryButton.hoverBorderColor : theme.colors.secondaryButton.borderColor;
        } else if (type == 'tertiary') {
            backgroundColor = 'transparent';
            borderWidth = 0;
            borderColor = undefined;
            paddingHorizontal = 0;
        } else if (type == 'destructive') {
            backgroundColor = disabled ? theme.colors.destructiveButton.disabledBackground : hovered ? theme.colors.destructiveButton.hoverBackground : theme.colors.destructiveButton.background;
            borderWidth = 0;
            borderColor = undefined;
        }

        return {
            flexDirection: 'row',
            alignItems: 'center',
            borderRadius: 5,
            borderWidth: borderWidth,
            borderColor: borderColor,
            paddingHorizontal: paddingHorizontal,
            width: '100%',
            columnGap: 10,
            height: 40,
            backgroundColor: backgroundColor,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            justifyContent: 'center',
            gap: 10
        };
    },

    buttonText: (type: ButtonTypes, hovered: boolean, disabled: boolean) => {

        let color: string | undefined;

        if (type == 'primary') {
            color = disabled ? theme.colors.primaryButton.disabledText : hovered ? theme.colors.primaryButton.hoverText : theme.colors.primaryButton.text;
        } else if (type == 'secondary') {
            color = disabled ? theme.colors.secondaryButton.disabledText : hovered ? theme.colors.secondaryButton.hoverText : theme.colors.secondaryButton.text;
        } else if (type == 'tertiary') {
            color = disabled ? theme.colors.secondaryButton.disabledText : hovered ? theme.colors.secondaryButton.hoverText : theme.colors.secondaryButton.text;
        } else if (type == 'destructive') {
            color = disabled ? theme.colors.destructiveButton.disabledText : hovered ? theme.colors.destructiveButton.hoverText : theme.colors.destructiveButton.text;
        }

        return {
            fontWeight: '600',
            color: color,
            fontSize: 16,
        };
    },

    buttonIcon: (type: ButtonTypes, hovered: boolean, disabled?: boolean, textStyle?: TextStyle) => {
        let color: string | undefined;

        if (type == 'primary') {
            color = disabled ? theme.colors.primaryButton.disabledText : hovered ? theme.colors.primaryButton.hoverText : theme.colors.primaryButton.text;
        } else if (type == 'secondary') {
            color = disabled ? theme.colors.secondaryButton.disabledText : hovered ? theme.colors.secondaryButton.hoverText : theme.colors.secondaryButton.text;
        } else if (type == 'tertiary') {
            color = disabled ? theme.colors.secondaryButton.disabledText : hovered ? theme.colors.secondaryButton.hoverText : theme.colors.secondaryButton.text;
        } else if (type == 'destructive') {
            color = disabled ? theme.colors.destructiveButton.disabledText : hovered ? theme.colors.destructiveButton.hoverText : theme.colors.destructiveButton.text;
        }

        if (textStyle?.color) {
            color = String(textStyle?.color);
        }

        return {
            color,
        };
    },

    loadingColor: {
        color: theme.colors.loadingColor
    }
}));
